import { useEffect, useState } from "react";
import { ref, onValue, update } from "firebase/database";
import { db } from "../firebase";
import Notification from "../Components/Notification.js";
import { useTheme } from "@mui/material";

const useDeployments = (user, showArchive) => {
  const [deployments, setDeployments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [completedDeploymentsCount, setCompletedDeploymentsCount] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    if (user) {
      setLoadingData(true);
      let activeSubscriptions = [];

      const handleSnapshot = (snapshot, isArchive) => {
        const data = snapshot.val();
        if (data) {
          let formattedData = Object.keys(data).map((key) => ({
            ...data[key],
            id: key,
            isArchive,
          }));

          formattedData.sort(
            (a, b) => new Date(b.startTime) - new Date(a.startTime),
          );

          setDeployments((prevDeployments) => [
            ...prevDeployments.filter((d) => d.isArchive !== isArchive),
            ...formattedData,
          ]);

          // Count only completed deployments that are not archived
          if (!isArchive) {
            const completedCount = formattedData.filter(
              (deployment) => deployment.percentComplete === 100 && !deployment.isArchive,
            ).length;
            setCompletedDeploymentsCount(completedCount);
          }
        }
        setLoadingData(false);
        setInitialLoad(false);
      };

      const subscribeToRef = (refPath, isArchive = false) => {
        const dbRef = ref(db, refPath);
        const unsubscribe = onValue(dbRef, (snapshot) =>
          handleSnapshot(snapshot, isArchive),
        );
        activeSubscriptions.push(unsubscribe);
      };

      setDeployments([]);
      setLoadingData(true);

      if (showArchive) {
        subscribeToRef("Deployments/");
        subscribeToRef("Archive/", true);
      } else {
        subscribeToRef("Deployments/");
      }

      return () => {
        activeSubscriptions.forEach((unsubscribe) => unsubscribe());
      };
    }
  }, [user, showArchive]);

  const archiveCompletedDeployments = async () => {
    const completedDeployments = deployments.filter(
      (deployment) =>
        deployment.percentComplete === 100 && !deployment.isArchive,
    );
    if (completedDeployments.length === 0) {
      return;
    }

    const result = await Notification.confirmArchiveAllDeployments(theme);

    if (result.isConfirmed) {
      const updates = {};
      completedDeployments.forEach((deployment) => {
        updates[`/Archive/${deployment.id}`] = deployment;
        updates[`/Deployments/${deployment.id}`] = null;
      });

      update(ref(db), updates)
        .then(() => {
          Notification.success(
            theme,
            "Archived!",
            "The completed deployments have been archived.",
          );
          setDeployments(
            deployments.filter(
              (deployment) => deployment.percentComplete !== 100,
            ),
          );
          setCompletedDeploymentsCount(0);
        })
        .catch((error) => {
          console.error("Error archiving deployments:", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem archiving the deployments.",
          );
        });
    }
  };

  return {
    deployments,
    loadingData,
    initialLoad,
    completedDeploymentsCount,
    archiveCompletedDeployments,
    setDeployments,
  };
};

export default useDeployments;
